import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
} from "@mui/material";
import { CustomGridToolbarQuickFilter } from "../../../../components/datagrid/CustomGridToolbarQuickFilter";
import { Trans, useTranslation } from "react-i18next";
import { DataEntryRoleContext } from "../../../../contexts/DataEntryRoleContext";
import { common, input } from "../../../../i18n";
import alertService from "../../../../components/alert/alertService";
import ActivitiesService from "../../../../service/activities.service";
import type { DeleteActivityResponse } from "../../../../models/dto/response/DeleteActivityResponse";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";

export const QuickSearchToolbar = () => {
  const { t } = useTranslation([common, input], {
    nsMode: "fallback",
  });

  const {
    selectionModel,
    setSelectionModel,
    rows,
    year,
    setActivities,
    setUpSumScopes,
  } = DataEntryRoleContext();

  const mutipleDeleteActivities = async () => {
    const totalDeleteRow: number = selectionModel.length;

    const isConfirmed = await alertService.confirm(
      t("Multiple-Delete"),
      <Trans
        t={t}
        i18nKey="Confirm-Delete-Multiple-GHG-Records"
        values={{ id: totalDeleteRow }}
        components={{
          span: <span style={{ color: "var(--red)" }} />,
        }}
      />,
      t("Delete"),
      "var(--red)",
    );

    if (isConfirmed) {
      setSelectionModel([]);
      let result: DeleteActivityResponse | undefined;
      try {
        result = await ActivitiesService.multipleSoftDelete(
          rows.filter((s) => selectionModel.includes(s.id)).map((s) => s._id),
          year,
        );
      } catch {
        // error alert already shown by service
        return;
      }

      if (result) {
        const failKeys: string[] = Object.keys(
          result.results.remark.deleteFail,
        );

        const failRecords: number = Object.values(
          result.results.remark.deleteFail,
        ).reduce((prev: number, curr: number) => {
          return prev + curr;
        }, 0);

        setActivities(result.results.activities);

        setUpSumScopes(result.results.sumScopes);

        alertService.confirm(
          t("Multiple-Delete"),
          <Box>
            <TableContainer sx={{ width: "100%" }}>
              <Table
                sx={{
                  "& .MuiTableCell-root:first-child": {
                    paddingX: 0,
                    paddingY: "20px",
                  },
                  "& .MuiTableCell-root": {
                    paddingLeft: "40px",
                    paddingRight: "8px",
                    paddingY: "20px",
                    verticalAlign: "top",
                  },
                  "& .MuiTableCell-root:last-child": {
                    paddingY: "20px",
                    paddingX: 0,
                  },
                }}
              >
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row" colSpan={2}>
                      <Typography
                        variant="text1"
                        sx={{
                          fontSize: "16px !important",
                          lineHeight: "24px !important",
                          fontWeight: 500,
                          fontFamily: "Inter",
                        }}
                      >{`${t("Number-Of-Delete-Records")}`}</Typography>
                    </TableCell>
                    <TableCell component="th" scope="row" align="right">
                      <Typography
                        variant="text1"
                        sx={{
                          fontSize: "16px !important",
                          lineHeight: "24px !important",
                          fontWeight: 500,
                          fontFamily: "Inter",
                        }}
                      >
                        {totalDeleteRow}
                      </Typography>
                    </TableCell>
                    <TableCell component="th" scope="row">
                      <Typography
                        variant="text1"
                        sx={{
                          fontSize: "16px !important",
                          lineHeight: "24px !important",
                          fontWeight: 500,
                          fontFamily: "Inter",
                        }}
                      >{`${t("Records")}`}</Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>

                <TableBody
                  sx={{
                    "& .MuiTableCell-root": {
                      borderBottom: "none",
                      paddingTop: "20px !important",
                      paddingBottom: "0px !important",
                    },
                  }}
                >
                  <TableRow>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        width: "24px",
                      }}
                    >
                      <CheckCircleIcon
                        color="success"
                        sx={{
                          fontSize: "24px",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      component="th"
                      scope="row"
                      sx={{
                        sm: {
                          minWidth: "auto",
                        },
                        md: {
                          minWidth: "400px",
                        },
                        paddingLeft: "8px !important",
                        paddingRight: "0 !important",
                      }}
                    >
                      <Typography variant="text1">{`${t(
                        "Delete-Success",
                      )}`}</Typography>
                    </TableCell>
                    <TableCell component="th" scope="row" align="right">
                      {result.results.remark.deleteSuccess}
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {t("Records")}
                    </TableCell>
                  </TableRow>

                  {failKeys.map((keys, index) => (
                    <TableRow
                      sx={{
                        display:
                          Object.values(result!.results.remark.deleteFail)[
                            index
                          ] > 0
                            ? "auto"
                            : "none",
                      }}
                    >
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          width: "24px",
                        }}
                      >
                        <ErrorIcon
                          color="error"
                          sx={{
                            fontSize: "24px",
                          }}
                        />
                      </TableCell>
                      <TableCell
                        component="th"
                        scope="row"
                        sx={{
                          paddingLeft: "8px !important",
                          paddingRight: "0 !important",
                        }}
                      >
                        <Typography variant="text1">{`${t(
                          "Delete-Fail",
                        )}`}</Typography>{" "}
                        <br />
                        <Typography
                          variant="caption"
                          color={"var(--gray1)"}
                        >{`${t("Cannot-Delete-Recurring-Item", {
                          param: t(keys),
                        })}`}</Typography>
                      </TableCell>
                      <TableCell component="th" scope="row" align="right">
                        {Object.values(result!.results.remark.deleteFail)[index]}
                      </TableCell>
                      <TableCell component="th" scope="row" width={"auto"}>
                        {t("Records")}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>,
          undefined,
          undefined,
          false,
          "auto",
        );
      }
    }
  };

  return (
    <Box
      display={"flex"}
      flexDirection={"column"}
      gap={"40px"}
      marginBottom={"10px"}
    >
      <Box
        display={"flex"}
        flexDirection={{ xs: "column-reverse", sm: "row" }}
        justifyContent={"space-between"}
        alignItems={"self-end"}
        gap={4}
      >
        <Box
          display={"flex"}
          columnGap={"12px"}
          flex={1}
          alignItems={"self-end"}
          alignContent={"stretch"}
          width={{ xs: "100%", sm: "auto" }}
        >
          <CustomGridToolbarQuickFilter />

          {selectionModel.length > 0 && (
            <IconButton onClick={() => mutipleDeleteActivities()}>
              <img src="/img/trash.svg" alt="trash" />
            </IconButton>
          )}
        </Box>
      </Box>
    </Box>
  );
};
